import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCustomerType } from '../context/CustomerTypeContext';
import * as orderService from '../lib/services/orderService';
import './Checkout.css';

const normalizeSwedishPhone = (value = '') => String(value).replace(/[\s()-]/g, '');

const isValidSwedishPhoneNumber = (value = '') => {
  const normalized = normalizeSwedishPhone(value);
  if (!normalized) return false;

  if (/^\+46[1-9]\d{6,11}$/.test(normalized)) return true;
  if (/^0046[1-9]\d{6,11}$/.test(normalized)) return true;
  if (/^0[1-9]\d{6,11}$/.test(normalized)) return true;

  return false;
};

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getTotalVAT, getCartPricing, clearCart } = useCart();
  const { isCorporate } = useCustomerType();
  const pricing = getCartPricing();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [deliveryMode, setDeliveryMode] = useState('home'); // 'home' | 'pickup'
  const [companyInfo, setCompanyInfo] = useState(null);
  const lastPickupNote = useRef('');
  const [shippingCalculating, setShippingCalculating] = useState(false);
  const [shippingCalculated, setShippingCalculated] = useState(false);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [shippingDistance, setShippingDistance] = useState(null);
  const [shippingError, setShippingError] = useState(null);
  const [shippingNote, setShippingNote] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    pincode: '',
    country: 'Sweden',
    notes: '',
  });

  useEffect(() => {
    if (isCorporate) {
      navigate('/products');
    }
  }, [isCorporate, navigate]);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const API_BASE_URL = process.env.NODE_ENV === 'production'
          ? (process.env.REACT_APP_API_URL || '/api')
          : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');
        const res = await fetch(`${API_BASE_URL}/admin/company-info`);
        if (res.ok) {
          const data = await res.json();
          setCompanyInfo(data);
        }
      } catch (err) {
        // company info is optional for pickup note; silently ignore
      }
    };
    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    if (deliveryMode === 'home') {
      setShippingCalculated(false);
      setShippingCharge(0);
      setShippingDistance(null);
      setShippingError(null);
      setShippingNote(null);
    }
  }, [pricing.subtotalAfterOffers, deliveryMode]);

  const buildPickupNote = (name, info) => {
    const pickupAddress = info?.address || 'our store location';
    const pickupPhone = info?.contact_information || 'our store phone number';

    return `This is a self-pickup order.

Pickup Address: ${pickupAddress}
Phone: ${pickupPhone}

Kindly call us before arriving to collect your order.`;
  };

  const handleDeliveryModeChange = (mode) => {
    setDeliveryMode(mode);
    setError(null);
    // Always reset shipping state on mode change
    setShippingCalculated(false);
    setShippingCharge(0);
    setShippingDistance(null);
    setShippingError(null);
    setShippingNote(null);

    // Clear notes on every toggle, then populate for pickup mode
    lastPickupNote.current = '';
    if (mode === 'pickup') {
      const note = buildPickupNote(formData.customer_name, companyInfo);
      lastPickupNote.current = note;
      setFormData((prev) => ({
        ...prev,
        address_line1: '',
        address_line2: '',
        city: '',
        pincode: '',
        country: '',
        notes: note,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        country: 'Sweden',
        notes: '',
      }));
    }
  };

  // Keep the pickup note in sync when customer name changes while in pickup mode
  useEffect(() => {
    if (deliveryMode !== 'pickup') return;
    const newNote = buildPickupNote(formData.customer_name, companyInfo);
    if (newNote === lastPickupNote.current) return;
    setFormData((prev) => ({
      ...prev,
      notes: prev.notes
        .replace(lastPickupNote.current, newNote),
    }));
    lastPickupNote.current = newNote;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customer_name, companyInfo]);

  if (isCorporate) {
    return null; // Will redirect
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Reset shipping when address changes
    if (['address_line1', 'address_line2', 'city', 'pincode'].includes(name)) {
      setShippingCalculated(false);
      setShippingCharge(0);
      setShippingDistance(null);
      setShippingError(null);
      setShippingNote(null);
    }
  };

  const handleCalculateShipping = async () => {
    setShippingError(null);

    // Validate address fields
    if (!formData.address_line1 || !formData.city || !formData.pincode) {
      setShippingError('Please fill in all address fields (Address, City, Postal Code) before calculating shipping.');
      return;
    }

    setShippingCalculating(true);

    try {
      // Get API base URL
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? (process.env.REACT_APP_API_URL || '/api')
        : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

      const subtotal = pricing.subtotalAfterOffers;

      const response = await fetch(`${API_BASE_URL}/orders/calculate-shipping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address_line1: formData.address_line1,
          address_line2: formData.address_line2,
          city: formData.city,
          pincode: formData.pincode,
          country: formData.country,
          subtotal: subtotal
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();

      if (data.outsideStockholm) {
        setShippingNote(null);
        setShippingDistance(typeof data.distance === 'number' ? data.distance : 0);
        setShippingCharge(typeof data.charge === 'number' ? data.charge : 0);
        setShippingError(null);
      } else if (data.charge === null) {
        setShippingError(data.message);
        setShippingNote(null);
        setShippingDistance(data.distance);
        setShippingCharge(0);
      } else {
        setShippingNote(null);
        setShippingCharge(data.charge);
        setShippingDistance(data.distance);
        setShippingError(null);
      }

      setShippingCalculated(true);

    } catch (err) {
      console.error('Shipping calculation error:', err);
      setShippingError(err.message || 'Failed to calculate shipping. Please try again.');
      setShippingCalculated(false);
      setShippingCharge(0);
    } finally {
      setShippingCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      setError('Your cart is empty!');
      return;
    }

    if (deliveryMode === 'home' && !shippingCalculated) {
      setError('Please calculate shipping charges before placing your order.');
      return;
    }

    if (!isValidSwedishPhoneNumber(formData.customer_phone)) {
      setError('Please enter a valid Swedish phone number (e.g. 0701234567 or +46701234567).');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const isPickup = deliveryMode === 'pickup';
      // For self pickup, do not send customer address details.
      const shipping_address = isPickup
        ? 'Self Pickup'
        : [
            formData.address_line1,
            formData.address_line2,
            formData.city,
            formData.pincode,
            formData.country
          ].filter(field => field.trim() !== '').join(', ');

      const orderItems = pricing.pricedItems.flatMap((item) => {
        const bundledQty = Math.max(0, Number(item.bundled_quantity || 0));
        const unbundledQty = Math.max(0, Number(item.unbundled_quantity || 0));
        const bundledUnitPrice = bundledQty > 0
          ? Number((Number(item.bundled_line_total || 0) / bundledQty).toFixed(2))
          : 0;
        const unbundledUnitPrice = Number(item.unbundled_unit_price || item.adjusted_unit_price || 0);

        if (bundledQty > 0 && unbundledQty > 0) {
          return [
            {
              product_id: item.id,
              quantity: bundledQty,
              price: bundledUnitPrice,
            },
            {
              product_id: item.id,
              quantity: unbundledQty,
              price: unbundledUnitPrice,
            },
          ];
        }

        return [{
          product_id: item.id,
          quantity: item.quantity,
          price: Number(item.adjusted_unit_price || 0),
        }];
      });

      const effectiveShippingCharge = deliveryMode === 'pickup' ? 0 : shippingCharge;

      const orderData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        shipping_address: shipping_address,
        address_line1: isPickup ? null : formData.address_line1,
        address_line2: isPickup ? null : (formData.address_line2 || null),
        city: isPickup ? null : formData.city,
        pincode: isPickup ? null : formData.pincode,
        country: isPickup ? null : formData.country,
        notes: formData.notes?.trim() || null,
        items: orderItems,
        discount: 0,
        total_amount: pricing.subtotalAfterOffers,
        shipping_charges: effectiveShippingCharge,
        shipping_note: deliveryMode === 'pickup' ? null : (shippingNote || null)
      };

      console.log('Placing order with data:', orderData);

      //console.log('Placing order with data:', formData.notes);
      //console.log('Placing order with data:', orderData.notes);

      const response = await orderService.createOrder(orderData);
      clearCart();
      setSuccess(true);
      setOrderId(response.order_number);

      console.log('✅ Order placed successfully:', response.order_number);
      
      // Redirect to home after 15 seconds
      setTimeout(() => {
        navigate('/');
      }, 15000);
    } catch (error) {
      console.error('Error placing order:', error);
      
      // Display error message to user
      if (error.response?.error) {
        setError(error.response.error);
      } else if (error.message) {
        setError(error.message);
      } else if (error.response?.status === 400) {
        setError('There was a problem with your order. Please check the items and try again.');
      } else {
        setError('Failed to place order. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && !success) {
    return (
      <div className="checkout-container">
        <section className="checkout-hero">
          <div className="container">
            <h1>Checkout</h1>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div className="empty-cart">
              <p>Your cart is empty. Please add items before checkout.</p>
              <button onClick={() => navigate('/products')} className="btn btn-primary">
                Browse Products
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <section className="checkout-hero">
        <div className="container">
          <h1>Checkout</h1>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {success ? (
            <div className="success-message-container">
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h2>Thanks for your order.</h2>
                {orderId && (
                  <p className="order-id-message">
                    <strong>Your order number is {orderId}.</strong>
                  </p>
                )}
                <p>Order confirmation with payment instructions has been sent to your email address with order details. Kindly check your inbox or spam folder.</p>
                <p>Our team will reach out to you regarding delivery.</p>
                <button onClick={() => navigate('/')} className="btn btn-primary">
                  Continue Shopping
                </button>
              </div>
            </div>
          ) : (
          <div className="checkout-content">
            <div className="checkout-form">
              <h2>Delivery Information</h2>
              
              {error && (
                <div className="error-message">
                  <strong>Error:</strong> {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* ── Delivery Mode ── */}
                <div className="delivery-mode-group">
                  <label className="delivery-mode-label">Delivery Option *</label>
                  <div className="delivery-mode-options">
                    <label
                      className={`delivery-mode-card${deliveryMode === 'home' ? ' selected' : ''}`}
                      onClick={() => handleDeliveryModeChange('home')}
                    >
                      <input
                        type="radio"
                        name="deliveryMode"
                        value="home"
                        checked={deliveryMode === 'home'}
                        onChange={() => handleDeliveryModeChange('home')}
                      />
                      <span className="delivery-mode-icon">🚚</span>
                      <span className="delivery-mode-title">Home Delivery</span>
                      <span className="delivery-mode-desc"><br/>We deliver to your door. Shipping fee calculated by distance.</span>
                    </label>
                    <label
                      className={`delivery-mode-card${deliveryMode === 'pickup' ? ' selected' : ''}`}
                      onClick={() => handleDeliveryModeChange('pickup')}
                    >
                      <input
                        type="radio"
                        name="deliveryMode"
                        value="pickup"
                        checked={deliveryMode === 'pickup'}
                        onChange={() => handleDeliveryModeChange('pickup')}
                      />
                      <span className="delivery-mode-icon">🏪</span>
                      <span className="delivery-mode-title">Self Pickup</span>
                      <span className="delivery-mode-desc"><br/>Pick up your order from our location. No shipping charge.</span>
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="customer_name">Full Name *</label>
                  <input
                    type="text"
                    id="customer_name"
                    name="customer_name"
                    required
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customer_email">Email Address *</label>
                  <input
                    type="email"
                    id="customer_email"
                    name="customer_email"
                    required
                    value={formData.customer_email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customer_phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="customer_phone"
                    name="customer_phone"
                    required
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    placeholder="0701234567 or +46701234567"
                    title="Enter a valid Swedish phone number"
                  />
                </div>

                {/* ── Address & Shipping (Home Delivery only) ── */}
                {deliveryMode === 'home' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="address_line1">Address Line 1 *</label>
                      <input
                        type="text"
                        id="address_line1"
                        name="address_line1"
                        required={deliveryMode === 'home'}
                        value={formData.address_line1}
                        onChange={handleInputChange}
                        placeholder="Street address, P.O. box"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="address_line2">Address Line 2</label>
                      <input
                        type="text"
                        id="address_line2"
                        name="address_line2"
                        value={formData.address_line2}
                        onChange={handleInputChange}
                        placeholder="Apartment, suite, unit, building, floor, etc."
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="city">City *</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          required={deliveryMode === 'home'}
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="City"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="pincode">Postal Code *</label>
                        <input
                          type="text"
                          id="pincode"
                          name="pincode"
                          required={deliveryMode === 'home'}
                          value={formData.pincode}
                          onChange={handleInputChange}
                          placeholder="Postal code"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="country">Country *</label>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <input
                          type="text"
                          id="country"
                          name="country"
                          required={deliveryMode === 'home'}
                          value={formData.country}
                          onChange={handleInputChange}
                          placeholder="Country"
                          style={{ flex: 1 }}
                          disabled
                        />
                        <button
                          type="button"
                          onClick={handleCalculateShipping}
                          disabled={shippingCalculating || !formData.address_line1 || !formData.city || !formData.pincode}
                          style={{
                            padding: '10px 16px',
                            marginTop: '2px',
                            backgroundColor: shippingCalculated ? '#22c55e' : '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            transition: 'background-color 0.3s ease',
                            opacity: (shippingCalculating || !formData.address_line1 || !formData.city || !formData.pincode) ? 0.5 : 1
                          }}
                        >
                          {shippingCalculating ? 'Calculating...' : shippingCalculated ? '✓ Calculated' : 'Calculate Shipping'}
                        </button>
                      </div>
                      {shippingError && (
                        <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '6px' }}>
                          {shippingError}
                        </p>
                      )}
                      {shippingNote && (
                        <p style={{ color: '#2563eb', fontSize: '0.85rem', marginTop: '6px' }}>
                          {shippingNote}
                        </p>
                      )}
                      {shippingCalculated && (
                        <p style={{ color: '#34d399', fontSize: '0.85rem', marginTop: '6px' }}>
                          ✓ Distance: {(shippingDistance ?? 0).toFixed(1)} km | Shipping: kr {shippingCharge.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label htmlFor="notes">
                    Order Notes {deliveryMode === 'pickup' ? '' : '(Optional)'}
                  </label>
                  
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    readOnly={deliveryMode === 'pickup'}
                    placeholder={deliveryMode === 'pickup' ? 'Pickup note is read-only for self pickup orders.' : 'Any special instructions for your order'}
                    rows={deliveryMode === 'pickup' ? 5 : 3}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-place-order"
                  disabled={loading || (deliveryMode === 'home' && !shippingCalculated)}
                  title={deliveryMode === 'home' && !shippingCalculated ? 'Calculate shipping charges first' : ''}
                >
                  {loading ? 'Placing Order...' : 'Place Order'}
                </button>
              </form>
            </div>

            <aside className="order-summary">
              <h2>Order Summary</h2>
              <div className="summary-items">
                {pricing.pricedItems.flatMap((item) => {
                  const hasSplit = item.bundled_quantity > 0 && item.unbundled_quantity > 0;

                  if (hasSplit) {
                    const bundledUnitPrice = item.bundled_line_total / item.bundled_quantity;
                    const unbundledUnitPrice = item.unbundled_unit_price;
                    return [
                      // Bundled row
                      <div key={`${item.id}-bundled`} className="summary-item">
                        <div className="item-details">
                          <h4>
                            {item.name}
                            <span className="checkout-bundle-tag">BUNDLE OFFER</span>
                          </h4>
                          {item.weight && item.weight > 0 && (
                            <p style={{ fontSize: '0.85em', color: '#666', margin: '2px 0' }}>📦 {item.weight}g</p>
                          )}
                          <p>Qty: {item.bundled_quantity} × kr {bundledUnitPrice.toFixed(2)}</p>
                          <p className="checkout-was-price">Was: kr {unbundledUnitPrice.toFixed(2)} each</p>
                        </div>
                        <div className="item-total">
                          kr {item.bundled_line_total.toFixed(2)}
                        </div>
                      </div>,
                      // Unbundled row
                      <div key={`${item.id}-unbundled`} className="summary-item">
                        <div className="item-details">
                          <h4>{item.name}</h4>
                          {item.weight && item.weight > 0 && (
                            <p style={{ fontSize: '0.85em', color: '#666', margin: '2px 0' }}>📦 {item.weight}g</p>
                          )}
                          <p>Qty: {item.unbundled_quantity} × kr {unbundledUnitPrice.toFixed(2)}</p>
                          {item.discounted_price && item.discounted_price < item.price && (
                            <p className="checkout-was-price">Was: kr {item.price.toFixed(2)} each</p>
                          )}
                        </div>
                        <div className="item-total">
                          kr {(item.unbundled_quantity * unbundledUnitPrice).toFixed(2)}
                        </div>
                      </div>,
                    ];
                  }

                  // Normal single row
                  const itemPrice = Number(item.adjusted_unit_price || 0);
                  return [
                    <div key={item.id} className="summary-item">
                      <div className="item-details">
                        <h4>{item.name}</h4>
                        {item.weight && item.weight > 0 && (
                          <p style={{ fontSize: '0.85em', color: '#666', margin: '2px 0' }}>📦 {item.weight}g</p>
                        )}
                        <p>Qty: {item.quantity} × kr {itemPrice.toFixed(2)}</p>
                        {item.discounted_price && item.discounted_price < item.price && (
                          <p className="checkout-was-price">Was: kr {item.price.toFixed(2)} each</p>
                        )}
                      </div>
                      <div className="item-total">
                        kr {(item.quantity * itemPrice).toFixed(2)}
                      </div>
                    </div>,
                  ];
                })}
              </div>
              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>kr {pricing.subtotalBeforeOffers.toFixed(2)}</span>
                </div>
                {pricing.offerDiscount > 0 && (
                  <div className="summary-row" style={{ color: '#166534' }}>
                    <span>Category offers:</span>
                    <span>- kr {pricing.offerDiscount.toFixed(2)}</span>
                  </div>
                )}
                <p className="tax-notice">
                  (Including kr {getTotalVAT().toFixed(2)} in taxes)
                </p>
                <div className="summary-row">
                    <span>Delivery:</span>
                    <span style={{ fontWeight: 600, color: deliveryMode === 'pickup' ? '#059669' : 'inherit' }}>
                      {deliveryMode === 'pickup' ? '🏪 Self Pickup' : '🚚 Home Delivery'}
                    </span>
                  </div>
                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>
                    {deliveryMode === 'pickup'
                      ? <span style={{ color: '#059669', fontWeight: 600 }}>FREE (Self Pickup)</span>
                      : !shippingCalculated
                      ? 'Calculate to proceed'
                      : shippingNote
                      ? 'To be confirmed'
                      : shippingCharge === 0
                      ? 'FREE'
                      : `kr ${shippingCharge.toFixed(2)}`}
                  </span>
                </div>
                {deliveryMode === 'home' && shippingNote && (
                  <div style={{ fontSize: '0.85rem', color: '#2563eb', marginTop: '8px', padding: '10px', backgroundColor: '#eff6ff', borderRadius: '4px', lineHeight: '1.5' }}>
                    {shippingNote}
                  </div>
                )}
                {deliveryMode === 'home' && shippingCalculated && (
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px', lineHeight: '1.6' }}>
                    <div><strong>Distance:</strong> {(shippingDistance ?? 0).toFixed(2)} km</div>
                    <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#999' }}>
                      <div>📍 Calculated based on delivery address</div>
                    </div>
                  </div>
                )}
                {deliveryMode === 'home' && (
                  <p className="shipping-notice">
                    *Click the "Calculate Shipping" button next to Country field to calculate shipping charges based on distance and order value.
                  </p>
                )}
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>kr {(pricing.subtotalAfterOffers + (deliveryMode === 'pickup' ? 0 : shippingCharge)).toFixed(2)}</span>
                </div>
              </div>
            </aside>
          </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Checkout;
